// FormulaRulesTab.js - Formula-based pricing (grills, complex calculations)
import {
  React,
  useEffect,
  useState,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC,
  useAttributeLabels
} from './PricingCommon';
import FormulaDiagram from './FormulaDiagram';
import { useProductScope } from '../utils/productScope';

const FormulaRulesTab = ({ highlightRule = '' }) => {
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const { canSeeScope } = useProductScope();

  const fetchData = () => {
    setLoading(true);
    authFetch('/pricing/formulas')
      .then(data => {
        setFormulas(data);
        setLoading(false);
        if (highlightRule && data?.length) {
          const match = data.find(r => r.code === highlightRule);
          if (match) {
            setSelectedRow(match);
            const idx = data.indexOf(match);
            setPaginationModel(prev => ({ ...prev, page: Math.floor(idx / prev.pageSize) }));
            setTimeout(() => {
              const el = document.querySelector(`[data-id="${match.id}"]`);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
          }
        }
      })
      .catch(err => { console.error('Failed to fetch formulas:', err); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (formData) => {
    try {
      let updatedRow;
      if (editData?.id) {
        updatedRow = await authFetchRequest(`/pricing/formulas/${editData.id}`, formData, 'PUT');
        setFormulas(prev => prev.map(row => row.id === editData.id ? updatedRow : row));
        if (selectedRow?.id === editData.id) setSelectedRow(updatedRow);
      } else {
        updatedRow = await authFetchRequest('/pricing/formulas', formData, 'POST');
        setFormulas(prev => [...prev, updatedRow]);
      }
      setOpenDialog(false);
      setEditData(null);

    } catch (err) {
      console.error('Save failed:', err);
      alertC(`Failed to save formula: ${err.message}`);
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this formula?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/pricing/formulas/${selectedRow.id}`, null, 'DELETE');
      setFormulas(prev => prev.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);

    } catch (err) {
      console.error('Delete failed:', err);
      alertC('Failed to delete formula');
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 50 },
    { field: 'code', headerName: 'Code', width: 150 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'formula', headerName: 'Formula', width: 250 },
    { field: 'applies_to', headerName: 'Applies To', width: 100 },
    { field: 'priority', headerName: 'Priority', width: 70 },
    { field: 'active', headerName: 'Active', width: 70, renderCell: (params) => params.value ? 'Yes' : 'No' }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Formula Rules</Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" size="small" onClick={() => { setEditData(null); setOpenDialog(true); }}>
          Add Formula
        </Button>
        <Button variant="contained" color="secondary" size="small" onClick={() => { setEditData(selectedRow); setOpenDialog(true); }} disabled={!selectedRow}>
          Edit Formula
        </Button>
        <Button variant="outlined" color="error" size="small" onClick={handleDelete} disabled={!selectedRow}>
          Delete Formula
        </Button>
      </Stack>

      <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <span style={{ fontSize: '0.85em', color: '#555' }}>
          Formulas allow complex pricing calculations using variables like width, height, H (horizontal bars), V (vertical bars), etc.
          Example: <code>(H+1) * (V+1) * 5.50</code> for grill pricing.
        </span>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={formulas.filter((r) => canSeeScope(r.applies_to))}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
          sx={{
            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#e3f2fd' },
            '& .MuiDataGrid-row': { cursor: 'pointer' }
          }}
          getRowClassName={(params) => params.row.id === selectedRow?.id ? 'Mui-selected' : ''}
        />
      )}

      <FormulaDialog
        open={openDialog}
        data={editData}
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

const FormulaDialog = ({ open, data, onClose, onSave }) => {
  const { pickDefault, optionsForValue } = useProductScope();
  const [lookups1D, setLookups1D] = useState([]);
  const [lookups2D, setLookups2D] = useState([]);
  const [showDiagram, setShowDiagram] = useState(false);
  const [invalidFields, setInvalidFields] = useState(new Set());
  const [formData, setFormData] = useState({
    code: '', name: '', description: '', formula: '',
    parameters: [],
    priceLookups: [],
    conditions: [],
    multiplicators: [],
    unit_price: 0, min_value: 0,
    applies_to: 'all', priority: 0, active: true
  });
  const { attributeLabels } = useAttributeLabels(formData.applies_to);

  const validateAttributes = () => {
    const invalid = new Set();
    const knownValues = new Set(attributeLabels.map(a => a.value));

    formData.parameters.forEach((param, i) => {
      if (param.attr && !knownValues.has(param.attr)) {
        invalid.add(`param-${i}`);
      }
    });

    formData.priceLookups.forEach((lookup, i) => {
      if (lookup.key1Attr && !knownValues.has(lookup.key1Attr)) {
        invalid.add(`lookup-key1-${i}`);
      }
      if (lookup.key2Attr && !knownValues.has(lookup.key2Attr)) {
        invalid.add(`lookup-key2-${i}`);
      }
    });

    formData.conditions.forEach((cond, i) => {
      if (cond.attr && !knownValues.has(cond.attr)) {
        invalid.add(`cond-${i}`);
      }
    });

    formData.multiplicators.forEach((mult, mi) => {
      (mult.conditions || []).forEach((cond, ci) => {
        if (cond.attr && !knownValues.has(cond.attr)) {
          invalid.add(`mult-${mi}-cond-${ci}`);
        }
      });
    });

    setInvalidFields(invalid);
  };

  useEffect(() => {
    authFetch('/pricing/lookups').then(data => setLookups1D(data || [])).catch(() => setLookups1D([]));
    authFetch('/pricing/lookups-2d').then(data => setLookups2D(data || [])).catch(() => setLookups2D([]));
  }, []);

  useEffect(() => {
    if (data) {
      setFormData({
        code: data.code || '',
        name: data.name || '',
        description: data.description || '',
        formula: data.formula || '',
        parameters: data.parameters || [],
        priceLookups: data.priceLookups || [],
        conditions: data.conditions || [],
        multiplicators: data.multiplicators || [],
        unit_price: data.unit_price || 0,
        min_value: data.min_value || 0,
        applies_to: data.applies_to || pickDefault('all'),
        priority: data.priority || 0,
        active: data.active ?? true
      });
    } else {
      setFormData({
        code: '', name: '', description: '', formula: '',
        parameters: [], priceLookups: [], conditions: [], multiplicators: [],
        unit_price: 0, min_value: 0,
        applies_to: pickDefault('all'), priority: 0, active: true
      });
    }
  }, [data, open]);

  // Parameter handlers
  const addParameter = () => {
    setFormData({ ...formData, parameters: [...formData.parameters, { attr: '', alias: '' }] });
  };
  const updateParameter = (index, field, value) => {
    const updated = [...formData.parameters];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, parameters: updated });
  };
  const removeParameter = (index) => {
    setFormData({ ...formData, parameters: formData.parameters.filter((_, i) => i !== index) });
  };

  // Price Lookup handlers
  const addPriceLookup = () => {
    setFormData({ ...formData, priceLookups: [...formData.priceLookups, { alias: '', type: '1d', lookupId: '', key1Attr: '', key2Attr: '' }] });
  };
  const updatePriceLookup = (index, field, value) => {
    const updated = [...formData.priceLookups];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'type' && value === '1d') {
      updated[index].key2Attr = '';
    }
    if (field === 'type') {
      updated[index].lookupId = '';
    }
    setFormData({ ...formData, priceLookups: updated });
  };
  const removePriceLookup = (index) => {
    setFormData({ ...formData, priceLookups: formData.priceLookups.filter((_, i) => i !== index) });
  };

  // Condition handlers
  const addCondition = () => {
    setFormData({ ...formData, conditions: [...formData.conditions, { attr: '', op: '=', value: '' }] });
  };
  const updateCondition = (index, field, value) => {
    const updated = [...formData.conditions];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, conditions: updated });
  };
  const removeCondition = (index) => {
    setFormData({ ...formData, conditions: formData.conditions.filter((_, i) => i !== index) });
  };

  // Multiplicator handlers
  const addMultiplicator = () => {
    setFormData({ ...formData, multiplicators: [...formData.multiplicators, { conditions: [{ attr: '', op: '=', value: '' }], multiplier: 1 }] });
  };
  const updateMultiplicator = (index, field, value) => {
    const updated = [...formData.multiplicators];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, multiplicators: updated });
  };
  const removeMultiplicator = (index) => {
    setFormData({ ...formData, multiplicators: formData.multiplicators.filter((_, i) => i !== index) });
  };
  const addMultiplicatorCondition = (multIndex) => {
    const updated = [...formData.multiplicators];
    updated[multIndex].conditions = [...(updated[multIndex].conditions || []), { attr: '', op: '=', value: '' }];
    setFormData({ ...formData, multiplicators: updated });
  };
  const updateMultiplicatorCondition = (multIndex, condIndex, field, value) => {
    const updated = [...formData.multiplicators];
    updated[multIndex].conditions[condIndex] = { ...updated[multIndex].conditions[condIndex], [field]: value };
    setFormData({ ...formData, multiplicators: updated });
  };
  const removeMultiplicatorCondition = (multIndex, condIndex) => {
    const updated = [...formData.multiplicators];
    updated[multIndex].conditions = updated[multIndex].conditions.filter((_, i) => i !== condIndex);
    setFormData({ ...formData, multiplicators: updated });
  };

  const isConditionsEmpty = formData.conditions.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Formula' : 'Add Formula'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Basic Info */}
          <Stack direction="row" spacing={2}>
            <TextField label="Code" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} fullWidth placeholder="e.g., w1_grillType" />
            <TextField label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth placeholder="e.g., Colonial Grill Pricing" />
          </Stack>
          <TextField label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth multiline rows={2} />

          {/* Parameters Section */}
          <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, bgcolor: '#e3f2fd' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                Parameters (define aliases for use in formula)
              </Typography>
              <Button size="small" variant="outlined" color="primary" onClick={addParameter}>+ Add Parameter</Button>
            </Stack>

            {formData.parameters.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                No parameters defined. Add parameters to use short aliases like H, V, mysquare in your formula.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {formData.parameters.map((param, index) => {
                  const selectedAttr = attributeLabels.find(a => a.value === param.attr);
                  const isInvalid = invalidFields.has(`param-${index}`);
                  return (
                    <Stack key={index} direction="row" spacing={1} alignItems="center">
                      <Autocomplete
                        size="small"
                        options={attributeLabels}
                        groupBy={(option) => option.category || 'general'}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                        value={selectedAttr || null}
                        onChange={(_, newValue) => updateParameter(index, 'attr', newValue?.value || '')}
                        filterOptions={(options, { inputValue }) => {
                          const filterText = inputValue.toLowerCase();
                          return options.filter(option =>
                            option.label?.toLowerCase().includes(filterText) ||
                            option.value?.toLowerCase().includes(filterText)
                          );
                        }}
                        renderInput={(params) => <TextField {...params} label="Attribute" placeholder="Search attributes..." error={isInvalid} />}
                        sx={{ minWidth: 350, flex: 1, ...(isInvalid && { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 } } }) }}
                        freeSolo
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') updateParameter(index, 'attr', value);
                        }}
                      />
                      <Typography variant="body2" sx={{ color: '#666', px: 1 }}>AS</Typography>
                      <TextField
                        size="small"
                        label="Alias"
                        value={param.alias || ''}
                        onChange={(e) => updateParameter(index, 'alias', e.target.value)}
                        placeholder="e.g., H, V, mysquare"
                        sx={{ width: 150, '& input': { fontFamily: 'monospace', fontWeight: 'bold' } }}
                      />
                      <Button size="small" color="error" onClick={() => removeParameter(index)} sx={{ minWidth: 30 }}>×</Button>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Price Lookups Section */}
          <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, bgcolor: '#f3e5f5' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                Price Lookups (use &lt;&lt;alias&gt;&gt; in formula to reference lookup values)
              </Typography>
              <Button size="small" variant="outlined" color="secondary" onClick={addPriceLookup}>+ Add Lookup</Button>
            </Stack>

            {formData.priceLookups.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                No price lookups. Add lookups to reference 1D or 2D price tables in your formula using &lt;&lt;price1&gt;&gt; syntax.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {formData.priceLookups.map((lookup, index) => {
                  const isKey1Invalid = invalidFields.has(`lookup-key1-${index}`);
                  const isKey2Invalid = invalidFields.has(`lookup-key2-${index}`);
                  return (
                  <Box key={index} sx={{ border: '1px solid #ce93d8', borderRadius: 1, p: 1.5, bgcolor: 'white' }}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          size="small"
                          label="Alias"
                          value={lookup.alias || ''}
                          onChange={(e) => updatePriceLookup(index, 'alias', e.target.value)}
                          placeholder="e.g., price1"
                          sx={{ width: 120, '& input': { fontFamily: 'monospace', fontWeight: 'bold' } }}
                          helperText="Use <<alias>> in formula"
                        />
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel>Type</InputLabel>
                          <Select value={lookup.type || '1d'} onChange={(e) => updatePriceLookup(index, 'type', e.target.value)} label="Type">
                            <MenuItem value="1d">1D Lookup</MenuItem>
                            <MenuItem value="2d">2D Lookup</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Lookup Table</InputLabel>
                          <Select
                            value={lookup.lookupId || ''}
                            onChange={(e) => updatePriceLookup(index, 'lookupId', e.target.value)}
                            label="Lookup Table"
                          >
                            {(lookup.type === '2d' ? lookups2D : lookups1D).map(l => (
                              <MenuItem key={l.id} value={l.id}>{l.name || l.code}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button size="small" color="error" onClick={() => removePriceLookup(index)} sx={{ minWidth: 30 }}>×</Button>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Autocomplete
                          size="small"
                          options={attributeLabels}
                          groupBy={(option) => option.category || 'general'}
                          getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                          value={attributeLabels.find(a => a.value === lookup.key1Attr) || null}
                          onChange={(_, newValue) => updatePriceLookup(index, 'key1Attr', newValue?.value || '')}
                          filterOptions={(options, { inputValue }) => {
                            const filterText = inputValue.toLowerCase();
                            return options.filter(option =>
                              option.label?.toLowerCase().includes(filterText) ||
                              option.value?.toLowerCase().includes(filterText)
                            );
                          }}
                          renderInput={(params) => <TextField {...params} label={lookup.type === '2d' ? 'Row Key (key1)' : 'Lookup Key'} placeholder="Search attributes..." error={isKey1Invalid} />}
                          sx={{ minWidth: 280, flex: 1, ...(isKey1Invalid && { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 } } }) }}
                          freeSolo
                          onInputChange={(_, value, reason) => {
                            if (reason === 'input') updatePriceLookup(index, 'key1Attr', value);
                          }}
                        />
                        {lookup.type === '2d' && (
                          <Autocomplete
                            size="small"
                            options={attributeLabels}
                            groupBy={(option) => option.category || 'general'}
                            getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                            value={attributeLabels.find(a => a.value === lookup.key2Attr) || null}
                            onChange={(_, newValue) => updatePriceLookup(index, 'key2Attr', newValue?.value || '')}
                            filterOptions={(options, { inputValue }) => {
                              const filterText = inputValue.toLowerCase();
                              return options.filter(option =>
                                option.label?.toLowerCase().includes(filterText) ||
                                option.value?.toLowerCase().includes(filterText)
                              );
                            }}
                            renderInput={(params) => <TextField {...params} label="Column Key (key2)" placeholder="Search attributes..." error={isKey2Invalid} />}
                            sx={{ minWidth: 280, flex: 1, ...(isKey2Invalid && { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 } } }) }}
                            freeSolo
                            onInputChange={(_, value, reason) => {
                              if (reason === 'input') updatePriceLookup(index, 'key2Attr', value);
                            }}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Formula Expression */}
          <Typography variant="subtitle2" sx={{ mt: 1, mb: -1, fontWeight: 'bold' }}>Formula Expression</Typography>
          <Box sx={{ p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1, fontSize: '0.8rem', color: '#666' }}>
            <strong>Available aliases:</strong> {formData.parameters.length > 0
              ? formData.parameters.filter(p => p.alias).map(p => <code key={p.alias} style={{ marginRight: 8, backgroundColor: '#e3f2fd', padding: '2px 6px', borderRadius: 3 }}>{p.alias}</code>)
              : <span style={{ fontStyle: 'italic' }}>None defined yet</span>
            }
            {formData.priceLookups.length > 0 && (
              <>
                <br /><strong>Price lookups:</strong> {formData.priceLookups.filter(l => l.alias).map(l => (
                  <code key={l.alias} style={{ marginRight: 8, backgroundColor: '#f3e5f5', padding: '2px 6px', borderRadius: 3 }}>&lt;&lt;{l.alias}&gt;&gt;</code>
                ))}
              </>
            )}
          </Box>
          <TextField
            label="Formula"
            value={formData.formula}
            onChange={(e) => setFormData({ ...formData, formula: e.target.value })}
            fullWidth
            multiline
            rows={2}
            placeholder={formData.parameters.length > 0
              ? `e.g., (${formData.parameters[0]?.alias || 'H'} + 1) * 2 + mysquare`
              : "e.g., (H + 1) * (V + 1) - define parameters above first"}
            sx={{ '& textarea': { fontFamily: 'monospace' } }}
          />

          {/* Unit Price & Min Value */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Unit Price"
              type="number"
              value={formData.unit_price}
              onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })}
              sx={{ width: 150 }}
              inputProps={{ step: 0.01 }}
              helperText="Multiplied by formula result"
            />
            <TextField
              label="Min Value"
              type="number"
              value={formData.min_value}
              onChange={(e) => setFormData({ ...formData, min_value: parseFloat(e.target.value) || 0 })}
              sx={{ width: 150 }}
              inputProps={{ step: 0.01 }}
              helperText="Minimum price"
            />
          </Stack>

          {/* Conditions Section */}
          <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, bgcolor: '#fafafa' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                IF: Conditions (when to apply this formula)
              </Typography>
              <Button size="small" variant="outlined" onClick={addCondition}>+ Add Condition</Button>
            </Stack>

            {isConditionsEmpty ? (
              <Typography variant="body2" sx={{ color: '#2e7d32', fontStyle: 'italic' }}>
                Always applies (no conditions)
              </Typography>
            ) : (
              <Stack spacing={1}>
                {formData.conditions.map((cond, index) => {
                  const selectedAttr = attributeLabels.find(a => a.value === cond.attr);
                  const isCondInvalid = invalidFields.has(`cond-${index}`);
                  return (
                    <Stack key={index} direction="row" spacing={1} alignItems="center">
                      {index > 0 && <Typography variant="caption" sx={{ color: '#666', minWidth: 35 }}>AND</Typography>}
                      <Autocomplete
                        size="small"
                        options={attributeLabels}
                        groupBy={(option) => option.category || 'general'}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                        value={selectedAttr || null}
                        onChange={(_, newValue) => updateCondition(index, 'attr', newValue?.value || '')}
                        filterOptions={(options, { inputValue }) => {
                          const filterText = inputValue.toLowerCase();
                          return options.filter(option =>
                            option.label?.toLowerCase().includes(filterText) ||
                            option.value?.toLowerCase().includes(filterText)
                          );
                        }}
                        renderInput={(params) => <TextField {...params} label="Attribute" placeholder="Search..." error={isCondInvalid} />}
                        sx={{ minWidth: 280, ...(isCondInvalid && { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 } } }) }}
                        freeSolo
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') updateCondition(index, 'attr', value);
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select value={cond.op || '='} onChange={(e) => updateCondition(index, 'op', e.target.value)}>
                          <MenuItem value="=">=</MenuItem>
                          <MenuItem value="!=">!=</MenuItem>
                          <MenuItem value=">">{'>'}</MenuItem>
                          <MenuItem value="<">{'<'}</MenuItem>
                          <MenuItem value=">=">{'≥'}</MenuItem>
                          <MenuItem value="<=">{'≤'}</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField size="small" label="Value" value={cond.value || ''} onChange={(e) => updateCondition(index, 'value', e.target.value)} sx={{ minWidth: 150 }} />
                      <Button size="small" color="error" onClick={() => removeCondition(index)} sx={{ minWidth: 30 }}>×</Button>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Multiplicators Section */}
          <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, bgcolor: '#fff8e1' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                Multiplicators (price adjustments based on conditions)
              </Typography>
              <Button size="small" variant="outlined" color="warning" onClick={addMultiplicator}>+ Add Multiplicator</Button>
            </Stack>

            {formData.multiplicators.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                No multiplicators (price = formula × unit_price)
              </Typography>
            ) : (
              <Stack spacing={2}>
                {formData.multiplicators.map((mult, multIndex) => (
                  <Box key={multIndex} sx={{ border: '1px solid #ffcc80', borderRadius: 1, p: 1.5, bgcolor: 'white' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                        Multiplicator #{multIndex + 1}
                      </Typography>
                      <Button size="small" color="error" onClick={() => removeMultiplicator(multIndex)}>Remove</Button>
                    </Stack>

                    {/* Multiplicator conditions */}
                    <Stack spacing={1} sx={{ mb: 1 }}>
                      {(mult.conditions || []).map((cond, condIndex) => {
                        const selectedAttr = attributeLabels.find(a => a.value === cond.attr);
                        const isMultCondInvalid = invalidFields.has(`mult-${multIndex}-cond-${condIndex}`);
                        return (
                          <Stack key={condIndex} direction="row" spacing={1} alignItems="center">
                            {condIndex > 0 && <Typography variant="caption" sx={{ color: '#666', minWidth: 35 }}>AND</Typography>}
                            <Autocomplete
                              size="small"
                              options={attributeLabels}
                              groupBy={(option) => option.category || 'general'}
                              getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                              value={selectedAttr || null}
                              onChange={(_, newValue) => updateMultiplicatorCondition(multIndex, condIndex, 'attr', newValue?.value || '')}
                              filterOptions={(options, { inputValue }) => {
                                const filterText = inputValue.toLowerCase();
                                return options.filter(option =>
                                  option.label?.toLowerCase().includes(filterText) ||
                                  option.value?.toLowerCase().includes(filterText)
                                );
                              }}
                              renderInput={(params) => <TextField {...params} label="Attribute" placeholder="Search..." error={isMultCondInvalid} />}
                              sx={{ minWidth: 250, ...(isMultCondInvalid && { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 } } }) }}
                              freeSolo
                              onInputChange={(_, value, reason) => {
                                if (reason === 'input') updateMultiplicatorCondition(multIndex, condIndex, 'attr', value);
                              }}
                            />
                            <FormControl size="small" sx={{ minWidth: 80 }}>
                              <Select value={cond.op || '='} onChange={(e) => updateMultiplicatorCondition(multIndex, condIndex, 'op', e.target.value)}>
                                <MenuItem value="=">=</MenuItem>
                                <MenuItem value="!=">!=</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField size="small" label="Value" value={cond.value || ''} onChange={(e) => updateMultiplicatorCondition(multIndex, condIndex, 'value', e.target.value)} sx={{ minWidth: 120 }} />
                            <Button size="small" color="error" onClick={() => removeMultiplicatorCondition(multIndex, condIndex)} sx={{ minWidth: 30 }}>×</Button>
                          </Stack>
                        );
                      })}
                      <Button size="small" variant="text" onClick={() => addMultiplicatorCondition(multIndex)}>+ AND condition</Button>
                    </Stack>

                    {/* Multiplier value */}
                    <TextField
                      size="small"
                      label="Multiplier Value"
                      type="number"
                      value={mult.multiplier || 1}
                      onChange={(e) => updateMultiplicator(multIndex, 'multiplier', parseFloat(e.target.value) || 1)}
                      inputProps={{ step: 0.01 }}
                      sx={{ width: 150 }}
                      helperText="e.g., 1.0, 1.29, 1.43"
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {/* Other Settings */}
          <Stack direction="row" spacing={2}>
            <FormControl sx={{ minWidth: 140 }}>
              <InputLabel>Applies To</InputLabel>
              <Select value={formData.applies_to} onChange={(e) => setFormData({ ...formData, applies_to: e.target.value })} label="Applies To">
                {optionsForValue(formData.applies_to).map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Priority" type="number" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })} sx={{ width: 120 }} helperText="Higher = first" />
            <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="outlined" onClick={() => setShowDiagram(true)}>Diagram</Button>
        <Button variant="outlined" color="warning" onClick={validateAttributes}>Validate</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
      <FormulaDiagram
        open={showDiagram}
        onClose={() => setShowDiagram(false)}
        formData={formData}
      />
    </Dialog>
  );
};

export default FormulaRulesTab;
